import { type ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { Link, useMatches, useSearchParams } from 'react-router';
import type { Fund } from '@/types/fund';
import { Button } from '@/components/ui/button';
import {
  PageBreadcrumb,
  type BreadcrumbSegment,
} from '@/components/ui/PageBreadcrumb';
import { fundNavItems } from '@/config/entity-navigation';

interface BreadcrumbHandle {
  breadcrumbs?:
    | Array<{ label: ReactNode; href?: string }>
    | ((match: {
        params: Record<string, string>;
      }) => Array<{ label: ReactNode; href?: string }>);
}

interface FundHeaderProps {
  fund: Fund;
  fundId: string;
  hideEditButton?: boolean;
}

// URL-param breadcrumbs (tab → section → subsection)
function buildParamBreadcrumbs(
  fundHref: string,
  searchParams: URLSearchParams
): BreadcrumbSegment[] {
  const tab = searchParams.get('tab');
  const section = searchParams.get('section');
  const subsection = searchParams.get('subsection');

  const tabItem = tab ? fundNavItems.find((i) => i.value === tab) : undefined;
  if (!tabItem) return [];

  const breadcrumbs: BreadcrumbSegment[] = [
    {
      label: tabItem.label,
      href: section ? `${fundHref}?tab=${tab}` : undefined,
    },
  ];

  const sectionItem = section
    ? tabItem.children?.find((c) => c.value === section)
    : undefined;
  if (!sectionItem) return breadcrumbs;

  breadcrumbs.push({
    label: sectionItem.label,
    href: subsection ? `${fundHref}?tab=${tab}&section=${section}` : undefined,
  });

  const subsectionItem = subsection
    ? sectionItem.children?.find((g) => g.value === subsection)
    : undefined;
  if (subsectionItem) {
    breadcrumbs.push({ label: subsectionItem.label });
  }
  return breadcrumbs;
}

export function FundHeader({ fund, fundId, hideEditButton }: FundHeaderProps) {
  const matches = useMatches();
  const [searchParams] = useSearchParams();

  // Route-handle breadcrumbs (used by sub-pages like email compose)
  const routeBreadcrumbs = matches.flatMap((m) => {
    const handle = m.handle as BreadcrumbHandle | undefined;
    if (!handle?.breadcrumbs) return [];
    return typeof handle.breadcrumbs === 'function'
      ? handle.breadcrumbs(m as { params: Record<string, string> })
      : handle.breadcrumbs;
  });

  const fundName = fund.display_name || fund.name;
  const fundHref = `/app/funds/${fundId}`;

  const paramBreadcrumbs =
    routeBreadcrumbs.length === 0
      ? buildParamBreadcrumbs(fundHref, searchParams)
      : [];

  const allExtraSegments =
    routeBreadcrumbs.length > 0 ? routeBreadcrumbs : paramBreadcrumbs;
  const hasExtraSegments = allExtraSegments.length > 0;

  const segments: BreadcrumbSegment[] = [
    { label: 'Funds', href: '/app/funds' },
    {
      label: fundName,
      href: hasExtraSegments ? fundHref : undefined,
    },
    ...allExtraSegments,
  ];

  const actions =
    !hideEditButton && !hasExtraSegments ? (
      <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
        <Link to={`${fundHref}/edit`} aria-label="Edit Fund">
          <Pencil className="h-3.5 w-3.5" />
        </Link>
      </Button>
    ) : undefined;

  return <PageBreadcrumb segments={segments} actions={actions} />;
}
